<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

import { DropdownStringOption } from '@/types/inputs'
import { useCompanyStore } from '@/stores/company-store'
import { ResourceResponse, uuid } from '@/types/general'
import { Location } from '@/types/company'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'

import MySelect from '@/components/my-components/form/MySelect.vue'

interface Props {
    customerCompany?: uuid
}

const props = defineProps<Props>()

const locationId = defineModel<string | null>({ required: true })

const companyStore = useCompanyStore()
const { t } = useI18n()
const companyLocations = useLazyLoadedList<DropdownStringOption>(fetchLocations)

const customerLocations = ref<DropdownStringOption[]>([])

async function fetchLocations(): Promise<DropdownStringOption[]> {
    if (!props.customerCompany) {
        return companyStore.locations.map((location) => ({
            value: location.id,
            label: location.name,
        }))
    }

    const { data: response } = await axios.get<ResourceResponse<Location[]>>(
        window.route('company.locations.index', { company: props.customerCompany }),
    )

    return response.data.map((location) => {
        return {
            label: location.name,
            value: location.id,
        }
    })
}

watch(
    () => props.customerCompany,
    async () => {
        locationId.value = null
        companyLocations.reset()
        if (!customerLocations.value) return
        await companyLocations.fetch()
    },
)

onMounted(async () => await companyLocations.fetch())
</script>

<template>
    <MySelect
        v-model="locationId"
        :options="companyLocations.items.value"
        group-class="mt-4  mx-auto"
        :label="!props.customerCompany ? t('location') : t('customerLocations')"
        clear-button
        :placeholder="!props.customerCompany ? t('all') : undefined"
        :clear-option="!props.customerCompany ? t('all') : undefined"
        @focus="companyLocations.fetch()"
    />
</template>
